import { CheckOutlined, PlusCircleFilled } from '@ant-design/icons';
import { ESettingKey } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, DatePicker, Form } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { settingsAdd, settingsDelete } from '../../reducers/settings/reducer';
import { type ISettingRecord } from '../../reducers/settings/types';

interface IForm {
  startDate: Dayjs;
  endDate: Dayjs;
  shipResumeDate: Dayjs;
}

interface HolidayPeriodAddProps {
  editingHolidayPeriod?: ISettingRecord | null;
  onEditSubmitted?: () => void;
}

function HolidayPeriodAdd({
  editingHolidayPeriod = null,
  onEditSubmitted,
}: HolidayPeriodAddProps): React.ReactElement {
  const dispatch = useAppDispatch();
  const settingsInProcess = useSelector((s: RootState) => s.Settings.inProcess);

  const [form] = Form.useForm<IForm>();

  useEffect((): void => {
    if (editingHolidayPeriod) {
      form.setFieldsValue({
        startDate: dayjs(editingHolidayPeriod.value.startDate),
        endDate: dayjs(editingHolidayPeriod.value.endDate),
        shipResumeDate: dayjs(editingHolidayPeriod.value.shipResumeDate),
      });
    } else {
      form.resetFields();
    }
  }, [editingHolidayPeriod, form]);

  const onFinish = useCallback(
    ({ startDate, endDate, shipResumeDate }: IForm): void => {
      if (editingHolidayPeriod) {
        dispatch(
          settingsDelete(ESettingKey.HOLIDAY_PERIOD, editingHolidayPeriod.id),
        );
        onEditSubmitted?.();
      }

      dispatch(
        settingsAdd(ESettingKey.HOLIDAY_PERIOD, {
          startDate: startDate.format(EDateFormat.DATE),
          endDate: endDate.format(EDateFormat.DATE),
          shipResumeDate: shipResumeDate.format(EDateFormat.DATE),
        }),
      );
      form.resetFields();
    },
    [dispatch, editingHolidayPeriod, onEditSubmitted, form],
  );

  return (
    <Form form={form} layout="inline" onFinish={onFinish}>
      <Form.Item
        name="startDate"
        label="Start date"
        rules={[{ required: true, message: 'Start date is required' }]}
      >
        <DatePicker disabled={settingsInProcess} />
      </Form.Item>

      <Form.Item
        name="endDate"
        label="End date"
        dependencies={['startDate']}
        rules={[
          { required: true, message: 'End date is required' },
          {
            validator(_rule, value: Dayjs | undefined) {
              const startDate: Dayjs | undefined =
                form.getFieldValue('startDate');
              if (!value || !startDate || value.isAfter(startDate, 'day')) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error('End date must be after the start date'),
              );
            },
          },
        ]}
      >
        <DatePicker disabled={settingsInProcess} />
      </Form.Item>

      <Form.Item
        name="shipResumeDate"
        label="Shipping resumes"
        dependencies={['endDate']}
        rules={[
          { required: true, message: 'Shipping-resume date is required' },
          {
            validator(_rule, value: Dayjs | undefined) {
              const endDate: Dayjs | undefined = form.getFieldValue('endDate');
              if (!value || !endDate || !value.isBefore(endDate, 'day')) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(
                  'Shipping-resume date must be on or after the end date',
                ),
              );
            },
          },
        ]}
      >
        <DatePicker disabled={settingsInProcess} />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          icon={editingHolidayPeriod ? <CheckOutlined /> : <PlusCircleFilled />}
          disabled={settingsInProcess}
          loading={settingsInProcess}
        >
          {editingHolidayPeriod ? 'Save' : 'Add'}
        </Button>
      </Form.Item>
    </Form>
  );
}

export default HolidayPeriodAdd;
